import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Sparkles, Phone } from 'lucide-react';
import { Button } from '../ui/button';

export const ContactHero = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="relative min-h-[70vh] flex items-start justify-center overflow-hidden pt-24">
      {/* Advanced Multi-Layer Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900">
        {/* Animated Gradient Mesh */}
        <div className="absolute inset-0 opacity-60">
          <div className="absolute top-1/4 left-1/4 w-[700px] h-[700px] bg-gradient-to-br from-primary/50 to-primary-glow/40 rounded-full blur-3xl animate-gradient-shift" />
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-tl from-accent/60 to-accent-dark/40 rounded-full blur-3xl animate-gradient-shift-reverse" />
          <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] bg-gradient-to-r from-primary-glow/40 to-accent/30 rounded-full blur-3xl animate-pulse-glow" />
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary/30 rounded-full animate-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
          {[...Array(10)].map((_, i) => (
            <div
              key={`accent-${i}`}
              className="absolute w-1.5 h-1.5 bg-accent/40 rounded-full animate-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${12 + Math.random() * 8}s`,
              }}
            />
          ))}
        </div>

        {/* Rotating Geometric Shapes */}
        <div className="absolute top-20 right-20 w-40 h-40 border-4 border-accent/20 rounded-full animate-spin-slow" />
        <div className="absolute bottom-40 left-40 w-32 h-32 border-4 border-primary/20 rounded-full animate-float-rotate" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center space-y-6"
        >
          {/* Badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 backdrop-blur-sm shadow-lg">
              <Sparkles className="h-4 w-4 text-accent animate-pulse-slow" />
              <span className="text-sm font-medium bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Get In Touch</span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
            Contact <span className="gradient-text-shimmer">VTV Enterprises</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            We're here to help! Reach out to us through any of our communication channels and experience exceptional service.
          </p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-4"
          >
            <Button
              size="lg"
              className="bg-accent hover:bg-accent-dark glow-red group"
              onClick={() => {
                const contactSection = document.querySelector('section');
                const nextSection = contactSection?.nextElementSibling;
                nextSection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Phone className="h-5 w-5 mr-2" />
              Call Us Now
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path
            d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};
